import React, { useEffect, useRef } from 'react';

const LONG_PRESS_DELAY_MS = 500;
// Cancel when finger moves, e.g. when scrolling a list
const MOVE_TOLERANCE_PX = 10;

type Handlers = {
  onTouchStart: React.TouchEventHandler;
  onTouchMove: React.TouchEventHandler;
  onTouchEnd: React.TouchEventHandler;
  onTouchCancel: React.TouchEventHandler;
  onContextMenu: React.MouseEventHandler;
  onClickCapture: React.MouseEventHandler;
};

// Touch only, mouse interactions are unaffected
export function useLongPress(onLongPress: () => void): Handlers {
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const clear = () => {
    clearTimeout(timerRef.current);
    startRef.current = null;
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return {
    onTouchStart: (e) => {
      const touch = e.touches[0];
      firedRef.current = false;
      if (!touch || e.touches.length > 1) return clear();

      startRef.current = { x: touch.clientX, y: touch.clientY };
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        startRef.current = null;
        onLongPress();
      }, LONG_PRESS_DELAY_MS);
    },
    onTouchMove: (e) => {
      const touch = e.touches[0];
      const start = startRef.current;
      if (!touch || !start) return;

      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (Math.hypot(dx, dy) > MOVE_TOLERANCE_PX) clear();
    },
    onTouchEnd: clear,
    onTouchCancel: clear,
    // Suppress browser's own long press menu
    onContextMenu: (e) => {
      if (startRef.current || firedRef.current) e.preventDefault();
    },
    // Prevent the press from also opening the card. Clicks from portals
    // (e.g. the opened menu) bubble here too, so ignore those.
    onClickCapture: (e) => {
      if (!firedRef.current) return;
      if (!e.currentTarget.contains(e.target as Node)) return;
      firedRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
  };
}
